import React from "react";
import PageShell from "@/components/PageShell";
import { FadeUp, RevealLines } from "@/components/Reveal";
import { pressUrls } from "@/lib/api";
import { FileDown, Copy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const facts = [
  ["Legal name", "NewGenPharma AG"],
  ["Registered office", "Baarerstrasse 78, CH-6300 Zug, Switzerland"],
  ["Laboratories", "Basel-Landschaft, Switzerland"],
  ["Founded", "April 2023"],
  ["Focus", "Central-nervous-system stimulants · API intermediates"],
  ["Compound classes", "Phenethylamines · Tropane analogs · Eugeroics · 2-Aminoindanes"],
  ["Team size", "17 senior scientists"],
  ["Regulatory posture", "GMP-aligned · DMF-ready · ICH-Q3A/D compliant"],
  ["Languages", "English · German · French"],
];

const boilers = [
  {
    length: "Short (25 words)",
    text:
      "NewGenPharma AG is a Swiss pharmaceutical research group developing central-nervous-system stimulants and manufacturing GMP-aligned API intermediates from its facilities in Zug and Basel-Landschaft.",
  },
  {
    length: "Medium (60 words)",
    text:
      "NewGenPharma AG is a Swiss pharmaceutical research group founded in Zug in 2023. It develops central-nervous-system stimulants — phenethylamines, tropane analogs, eugeroics and 2-aminoindanes — and manufactures GMP-aligned API intermediates for academic and industry partners across Europe. Every batch carries a full Certificate of Analysis, ICH-Q3A/D residual profiles and DMF-ready documentation.",
  },
  {
    length: "Long (110 words)",
    text:
      "NewGenPharma AG is a Swiss pharmaceutical research group headquartered in Zug and with laboratories in Basel-Landschaft. Founded in 2023 by a team of senior medicinal chemists, process engineers and clinical scientists whose combined résumé spans three decades at tier-one Swiss and German houses, the group focuses on the discovery of central-nervous-system stimulants and on the manufacture of GMP-aligned API intermediates. Every batch leaves the facility with a full Certificate of Analysis, ICH-Q3A/D residual profiles and DMF-ready documentation. NewGenPharma serves academic collaborators, contract research organisations, and API buyers across Europe.",
  },
];

const images = [
  {
    url: "https://images.unsplash.com/photo-1763668331599-487470fb85b2",
    caption: "Product photography · white classical capsule bottle",
    filename: "ngp-capsule-bottle.jpg",
  },
  {
    url: "https://images.unsplash.com/photo-1532094349884-543bc11b234d",
    caption: "Laboratory glassware · Basel-Landschaft",
    filename: "ngp-lab-glassware.jpg",
  },
  {
    url: "https://images.unsplash.com/photo-1614595737476-42487331b8a1",
    caption: "Swiss architecture · Zug HQ",
    filename: "ngp-hq-architecture.jpg",
  },
  {
    url: "https://images.unsplash.com/photo-1656331797721-b593b8f00297",
    caption: "Molecular render · abstract compound",
    filename: "ngp-molecule.jpg",
  },
];

const copy = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied.`);
};

export default function Press() {
  return (
    <PageShell title="Press · Media Kit — NewGenPharma">
      {/* HEADER */}
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-16">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="font-mono-tight text-[11px] text-ink/60 flex items-center gap-4 mb-8">
              <span className="w-1.5 h-1.5 bg-oxred inline-block" />
              §05 · Press &amp; Media
            </div>
          </FadeUp>
          <RevealLines
            as="h1"
            className="font-display text-[13vw] md:text-[8vw] leading-[0.92] tracking-[-0.03em]"
            lines={["Press kit,", <>on the <em className="italic text-oxred">record.</em></>]}
          />
          <FadeUp delay={0.5}>
            <p className="mt-10 max-w-2xl text-ink/75 text-base leading-relaxed">
              Boilerplate, facts, logo pack and high-resolution imagery for journalists and analysts. Everything on
              this page is cleared for publication. For interview requests, factual corrections or asset queries,
              write to{" "}
              <a
                className="link-underline text-oxred"
                href="mailto:press@newgenpharma.ch"
                data-testid="press-media-email"
              >
                press@newgenpharma.ch
              </a>
              .
            </p>
          </FadeUp>
        </div>
      </section>

      {/* DOWNLOADS ROW */}
      <section data-testid="press-downloads" className="px-6 md:px-10 pb-16">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/15 border border-ink/15">
          {[
            {
              label: "Fact Sheet (PDF)",
              sub: "A4 · 1 page · updated automatically",
              href: pressUrls.factSheet,
              testid: "press-download-factsheet",
            },
            {
              label: "Logo · Light (SVG)",
              sub: "For light backgrounds · scalable vector",
              href: pressUrls.logoLight,
              testid: "press-download-logo-light",
            },
            {
              label: "Logo · Dark (SVG)",
              sub: "For dark backgrounds · scalable vector",
              href: pressUrls.logoDark,
              testid: "press-download-logo-dark",
            },
          ].map((d) => (
            <FadeUp key={d.testid}>
              <a
                href={d.href}
                target="_blank"
                rel="noreferrer"
                data-testid={d.testid}
                className="group block bg-bone p-10 md:p-14 hover:bg-bone-2 transition-colors duration-300 h-full"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-tight text-[10px] text-oxred">DOWNLOAD</span>
                  <FileDown
                    size={18}
                    className="transition-transform duration-500 group-hover:translate-y-1"
                  />
                </div>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-8 leading-[1]">{d.label}</h3>
                <p className="mt-4 font-mono-tight text-[10px] text-ink/60">{d.sub}</p>
              </a>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* FACTS */}
      <section data-testid="press-facts" className="px-6 md:px-10 py-24 border-y border-ink/10 bg-bone-2/50">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-8">
          <FadeUp className="md:col-span-4">
            <span className="font-mono-tight text-[11px] text-ink/60">§ At a glance</span>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4 leading-[0.98]">
              Group facts.
            </h2>
          </FadeUp>
          <FadeUp className="md:col-span-8" delay={0.15}>
            <dl className="divide-y divide-ink/10 border-y border-ink/15">
              {facts.map(([k, v]) => (
                <div key={k} className="grid grid-cols-12 gap-4 py-5">
                  <dt className="col-span-4 font-mono-tight text-[11px] text-ink/60 pt-1">{k}</dt>
                  <dd className="col-span-8 text-ink/90 leading-relaxed">{v}</dd>
                </div>
              ))}
            </dl>
          </FadeUp>
        </div>
      </section>

      {/* BOILERPLATE */}
      <section data-testid="press-boilerplate" className="px-6 md:px-10 py-32">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-12 border-b border-ink/15 pb-6">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Boilerplate</h2>
              <span className="font-mono-tight text-[11px] text-ink/60">
                Three lengths · click any to copy
              </span>
            </div>
          </FadeUp>

          <div className="space-y-10">
            {boilers.map((b, i) => (
              <FadeUp key={b.length}>
                <article className="grid grid-cols-1 md:grid-cols-12 gap-8 py-6 border-b border-ink/10">
                  <div className="md:col-span-3">
                    <div className="font-mono-tight text-[10px] text-oxred">0{i + 1}</div>
                    <div className="font-mono-tight text-[11px] text-ink/70 mt-2">{b.length}</div>
                  </div>
                  <div className="md:col-span-9">
                    <p className="text-ink/90 text-base md:text-lg leading-relaxed max-w-3xl">{b.text}</p>
                    <button
                      onClick={() => copy(b.text, b.length)}
                      data-testid={`press-copy-${i + 1}`}
                      className="mt-6 inline-flex items-center gap-2 font-mono-tight text-[10px] text-ink/70 link-underline"
                    >
                      <Copy size={12} /> Copy to clipboard
                    </button>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGERY */}
      <section data-testid="press-imagery" className="px-6 md:px-10 py-24 bg-bone-2/50 border-y border-ink/10">
        <div className="mx-auto max-w-[1600px]">
          <FadeUp>
            <div className="flex items-baseline justify-between mb-10 border-b border-ink/15 pb-6">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight">Imagery</h2>
              <span className="font-mono-tight text-[11px] text-ink/60">
                High-resolution · publication-ready
              </span>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {images.map((im) => (
              <FadeUp key={im.filename}>
                <a
                  href={im.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`press-image-${im.filename}`}
                  className="group block"
                >
                  <div className="relative aspect-[16/10] overflow-hidden border border-ink/15">
                    <img
                      src={im.url}
                      alt={im.caption}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 font-mono-tight text-[10px] text-bone bg-ink/70 px-2 py-1">
                      {im.filename}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm text-ink/80">{im.caption}</span>
                    <span className="font-mono-tight text-[10px] text-ink/60 inline-flex items-center gap-1">
                      Open <ArrowUpRight size={12} />
                    </span>
                  </div>
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="px-6 md:px-10 py-24">
        <div className="mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <FadeUp className="md:col-span-8">
            <span className="font-mono-tight text-[11px] text-ink/60">§ Media contact</span>
            <h3 className="font-display text-5xl md:text-7xl tracking-tight mt-6 leading-[0.98]">
              Talk to <em className="italic text-oxred">us.</em>
            </h3>
          </FadeUp>
          <FadeUp className="md:col-span-4" delay={0.15}>
            <div className="border border-ink/15 p-8">
              <div className="font-mono-tight text-[10px] text-ink/60">Press office</div>
              <a
                href="mailto:press@newgenpharma.ch"
                className="font-display text-2xl md:text-3xl tracking-tight mt-2 block link-underline"
              >
                press@newgenpharma.ch
              </a>
              <div className="mt-6 font-mono-tight text-[10px] text-ink/60">
                Response within 24h during Swiss business days
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
